import React, { useRef, useEffect, useCallback, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { getChatCompletion } from '../../../services/aiIntegrations/chatCompletion';
import toast from 'react-hot-toast';

const TOOLBAR_GROUPS = [
  [
    { cmd: 'bold', icon: 'Bold', title: 'Bold (Ctrl+B)' },
    { cmd: 'italic', icon: 'Italic', title: 'Italic (Ctrl+I)' },
    { cmd: 'underline', icon: 'Underline', title: 'Underline (Ctrl+U)' },
  ],
  [
    { cmd: 'justifyLeft', icon: 'AlignLeft', title: 'Align Left' },
    { cmd: 'justifyCenter', icon: 'AlignCenter', title: 'Center' },
    { cmd: 'justifyRight', icon: 'AlignRight', title: 'Align Right' },
  ],
  [
    { cmd: 'insertUnorderedList', icon: 'List', title: 'Bullet List' },
    { cmd: 'insertOrderedList', icon: 'ListOrdered', title: 'Numbered List' },
  ],
  [
    { cmd: 'indent', icon: 'Indent', title: 'Indent' },
    { cmd: 'outdent', icon: 'Outdent', title: 'Outdent' },
  ],
];

const HEADING_OPTIONS = [
  { label: 'Normal', tag: 'p' },
  { label: 'Heading 1', tag: 'h1' },
  { label: 'Heading 2', tag: 'h2' },
  { label: 'Heading 3', tag: 'h3' },
];

const VARIABLE_CATEGORIES = [
  {
    id: 'price',
    label: '💰 Pricing',
    description: 'Auto-filled from job — flat rate or hourly, whichever applies',
    vars: [
      { key: '{{total_cost}}', label: 'Total Cost', hint: 'Job total (flat rate)' },
      { key: '{{labor_cost}}', label: 'Labor Cost', hint: 'Labor portion' },
      { key: '{{materials_cost}}', label: 'Materials Cost', hint: 'Parts & materials' },
      { key: '{{hourly_rate}}', label: 'Hourly Rate', hint: 'Rate per hour' },
      { key: '{{duration}}', label: 'Hours Worked', hint: 'Number of hours' },
      { key: '{{subtotal}}', label: 'Subtotal', hint: 'Before tax' },
      { key: '{{tax}}', label: 'Tax Amount', hint: 'Calculated tax' },
      { key: '{{total}}', label: 'Grand Total', hint: 'Final total incl. tax' },
      { key: '{{payment_status}}', label: 'Payment Status', hint: 'Paid / Unpaid / Partial' },
      { key: '{{payment_terms}}', label: 'Payment Terms', hint: 'e.g. Net 30' },
      { key: '{{max_amount_due}}', label: 'Max Amount Due', hint: 'Maximum amount at delivery' },
      { key: '{{balance_due}}', label: 'Balance Due', hint: 'Remaining balance' },
    ],
  },
  {
    id: 'client',
    label: '👤 Client & Doc',
    description: null,
    vars: [
      { key: '{{company_name}}', label: 'Company Name', hint: 'Your business name' },
      { key: '{{client_name}}', label: 'Client Name', hint: 'Customer full name' },
      { key: '{{client_phone}}', label: 'Client Phone', hint: 'Customer phone number' },
      { key: '{{client_address}}', label: 'Client Address', hint: 'Customer address' },
      { key: '{{date}}', label: 'Document Date', hint: "Date contract/doc is created" },
      { key: '{{due_date}}', label: 'Due Date', hint: 'Payment due date' },
      { key: '{{invoice_number}}', label: 'Invoice #', hint: 'Auto-generated' },
      { key: '{{estimate_number}}', label: 'Estimate #', hint: 'Auto-generated' },
    ],
  },
  {
    id: 'job',
    label: '🔧 Job Details',
    description: null,
    vars: [
      { key: '{{job_title}}', label: 'Job Title', hint: 'Name of the job' },
      { key: '{{service_description}}', label: 'Service Description', hint: 'What was done' },
      { key: '{{service_type}}', label: 'Service Type', hint: 'Type of service' },
      { key: '{{location}}', label: 'Job Location', hint: 'Service address' },
      { key: '{{scheduled_date}}', label: 'Service Date Requested', hint: 'Date service is scheduled/requested' },
      { key: '{{delivery_date}}', label: 'Delivery Date Requested', hint: 'Requested delivery date' },
      { key: '{{frequency}}', label: 'Frequency', hint: 'Recurring schedule' },
      { key: '{{home_size}}', label: 'Home / Space Size', hint: 'Property size' },
      { key: '{{issue_type}}', label: 'Issue / Task Type', hint: 'HVAC, plumbing, handyman' },
      { key: '{{system_type}}', label: 'System Type', hint: 'HVAC system type' },
      { key: '{{num_movers}}', label: 'Number of Movers', hint: 'How many movers/workers' },
      { key: '{{num_trucks}}', label: 'Number of Trucks', hint: 'How many trucks/vans' },
    ],
  },
  {
    id: 'moving',
    label: '🚚 Moving / Auto',
    description: null,
    vars: [
      { key: '{{move_from}}', label: 'Moving From', hint: 'Origin address' },
      { key: '{{move_to}}', label: 'Moving To', hint: 'Destination address' },
      { key: '{{from_address}}', label: 'From Address', hint: 'Full origin address' },
      { key: '{{from_phone}}', label: 'From Phone', hint: 'Origin contact phone' },
      { key: '{{to_address}}', label: 'To Address', hint: 'Full destination address' },
      { key: '{{to_phone}}', label: 'To Phone', hint: 'Destination contact phone' },
      { key: '{{pickup_address}}', label: 'Pickup Address', hint: 'Origin (transport)' },
      { key: '{{delivery_address}}', label: 'Delivery Address', hint: 'Destination' },
      { key: '{{vehicle_type}}', label: 'Vehicle Type', hint: 'Type of vehicle' },
      { key: '{{vehicle_make_model}}', label: 'Vehicle Make/Model', hint: 'e.g. BMW X5' },
      { key: '{{vehicle_year}}', label: 'Vehicle Year', hint: 'Year of vehicle' },
      { key: '{{service_location}}', label: 'Service Location', hint: 'Where service is done' },
    ],
  },
];

export const AVAILABLE_VARIABLES = VARIABLE_CATEGORIES?.flatMap(c =>
  c?.vars?.map(v => ({ key: v?.key, label: v?.label }))
);

export const textToHtml = (text) => {
  if (!text) return '';
  if (text?.trim()?.startsWith('<')) return text;
  return text?.split('\n')?.map(line => {
    if (!line?.trim()) return '<p><br></p>';
    const escaped = line?.replace(/&/g, '&amp;')?.replace(/</g, '&lt;')?.replace(/>/g, '&gt;');
    return `<p>${escaped}</p>`;
  })?.join('');
};

export const htmlToText = (html) => {
  if (!html) return '';
  return html
    ?.replace(/<br\s*\/?>/gi, '\n')
    ?.replace(/<\/p>/gi, '\n')
    ?.replace(/<\/h[1-6]>/gi, '\n')
    ?.replace(/<\/li>/gi, '\n')
    ?.replace(/<\/div>/gi, '\n')
    ?.replace(/<[^>]+>/g, '')
    ?.replace(/&amp;/g, '&')
    ?.replace(/&lt;/g, '<')
    ?.replace(/&gt;/g, '>')
    ?.replace(/&nbsp;/g, ' ')
    ?.replace(/\n{3,}/g, '\n\n')
    ?.trim();
};

const AI_MODES = [
  { id: 'variables', label: 'Insert Variables', icon: 'Braces' },
  { id: 'improve', label: 'Fix & Improve', icon: 'Wand2' },
];

const VARIABLE_SUGGESTIONS = [
  'Insert all variables into this contract',
  'Add client and pricing variables',
  'Insert job details variables only',
  'Add variables to the payment section',
  'Replace all hardcoded names, dates and amounts with variables',
];

const IMPROVE_SUGGESTIONS = [
  { label: 'Fix all tables & rows', prompt: 'Fix all tables in this document — align columns, fix broken rows, ensure consistent borders and padding, make them look professional.' },
  { label: 'Improve text quality', prompt: 'Improve the text quality of this document — fix grammar, spelling, punctuation, and make the language more professional and clear. Keep all variables and structure intact.' },
  { label: 'Fix formatting & structure', prompt: 'Fix the overall formatting and structure of this document — fix inconsistent spacing, paragraph breaks, heading hierarchy, and layout. Keep all content and variables intact.' },
  { label: 'Make it more professional', prompt: 'Rewrite this document to sound more professional and formal. Improve sentence structure, remove informal language, and ensure consistent tone throughout. Keep all variables intact.' },
  { label: 'Fix & add missing variables', prompt: 'Fix the document and also insert any missing template variables where hardcoded values appear (names, dates, amounts, addresses). Keep all formatting and structure intact.' },
  { label: 'Clean up spacing & layout', prompt: 'Clean up all spacing issues in this document — remove extra blank lines, fix inconsistent margins, ensure proper paragraph spacing, and make the layout clean and consistent.' },
  { label: 'Rebuild broken tables', prompt: 'Rebuild all tables in this document with proper HTML structure — fix any broken or malformed tables, ensure all rows and columns are correct, add proper headers and styling.' },
  { label: 'Summarize & shorten', prompt: 'Shorten this document by removing redundant sentences and paragraphs while keeping all essential legal terms, variables, and key information intact.' },
];

const RichTextEditor = ({ value, onChange, placeholder, minHeight = 420 }) => {
  const editorRef = useRef(null);
  const isInternalChange = useRef(false);
  const savedSelection = useRef(null);
  const [activeFormats, setActiveFormats] = useState({});
  const [showVarPanel, setShowVarPanel] = useState(false);
  const [activeVarCategory, setActiveVarCategory] = useState('price');
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [aiMode, setAiMode] = useState('variables');
  const [aiInstruction, setAiInstruction] = useState('');
  const [aiProcessing, setAiProcessing] = useState(false);

  useEffect(() => {
    if (!editorRef?.current) return;
    const htmlValue = textToHtml(value);
    if (editorRef?.current?.innerHTML !== htmlValue && !isInternalChange?.current) {
      editorRef.current.innerHTML = htmlValue || '';
    }
  }, [value]);

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel?.rangeCount > 0) {
      savedSelection.current = sel?.getRangeAt(0)?.cloneRange();
    }
  };

  const restoreSelection = () => {
    if (savedSelection?.current) {
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(savedSelection?.current);
    }
  };

  const handleInput = useCallback(() => {
    if (!editorRef?.current) return;
    isInternalChange.current = true;
    const html = editorRef?.current?.innerHTML;
    onChange(html);
    setTimeout(() => { isInternalChange.current = false; }, 0);
  }, [onChange]);

  const updateActiveFormats = () => {
    setActiveFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      justifyLeft: document.queryCommandState('justifyLeft'),
      justifyCenter: document.queryCommandState('justifyCenter'),
      justifyRight: document.queryCommandState('justifyRight'),
      insertUnorderedList: document.queryCommandState('insertUnorderedList'),
      insertOrderedList: document.queryCommandState('insertOrderedList'),
    });
  };

  const execCmd = (cmd, val = null) => {
    editorRef?.current?.focus();
    document.execCommand(cmd, false, val);
    handleInput();
    updateActiveFormats();
  };

  const insertVariable = (varKey) => {
    editorRef?.current?.focus();
    restoreSelection();
    const span = document.createElement('span');
    span.className = 'var-chip';
    span.style.cssText = 'background:#eff6ff;color:#2563eb;border:1px solid #bfdbfe;border-radius:4px;padding:1px 6px;font-size:12px;font-family:monospace;white-space:nowrap;';
    span.contentEditable = 'false';
    span.textContent = varKey;
    document.execCommand('insertHTML', false, span?.outerHTML + '&nbsp;');
    handleInput();
    setShowVarPanel(false);
  };

  const insertDivider = () => {
    execCmd('insertHTML', '<hr style="border:none;border-top:2px solid #e5e7eb;margin:12px 0;"/>');
  };

  const insertSignatureLine = () => {
    execCmd('insertHTML', '<p style="margin-top:24px;"><span style="display:inline-block;min-width:220px;border-bottom:1px solid #374151;">&nbsp;</span>&nbsp;&nbsp;Date: <span style="display:inline-block;min-width:100px;border-bottom:1px solid #374151;">&nbsp;</span></p><p style="font-size:12px;color:#6b7280;">Signature &amp; Date</p>');
  };

  const insertTable = () => {
    execCmd('insertHTML', `<table style="width:100%;border-collapse:collapse;margin:12px 0;font-size:13px;"><thead><tr style="background:#f9fafb;"><th style="border:1px solid #e5e7eb;padding:8px 12px;text-align:left;font-weight:600;">Description</th><th style="border:1px solid #e5e7eb;padding:8px 12px;text-align:center;width:80px;">Qty</th><th style="border:1px solid #e5e7eb;padding:8px 12px;text-align:right;width:100px;">Price</th><th style="border:1px solid #e5e7eb;padding:8px 12px;text-align:right;width:100px;">Total</th></tr></thead><tbody><tr><td style="border:1px solid #e5e7eb;padding:8px 12px;">{{service_description}}</td><td style="border:1px solid #e5e7eb;padding:8px 12px;text-align:center;">1</td><td style="border:1px solid #e5e7eb;padding:8px 12px;text-align:right;">{{total_cost}}</td><td style="border:1px solid #e5e7eb;padding:8px 12px;text-align:right;">{{total_cost}}</td></tr></tbody></table>`);
  };

  const insertFlatPricingTable = () => {
    execCmd('insertHTML', `<table style="width:100%;border-collapse:collapse;margin:16px 0;font-size:13px;"><thead><tr style="background:#f9fafb;"><th style="border:1px solid #e5e7eb;padding:8px 12px;text-align:left;font-weight:600;">Description</th><th style="border:1px solid #e5e7eb;padding:8px 12px;text-align:right;width:130px;font-weight:600;">Amount</th></tr></thead><tbody><tr><td style="border:1px solid #e5e7eb;padding:8px 12px;">Labor</td><td style="border:1px solid #e5e7eb;padding:8px 12px;text-align:right;font-family:monospace;">$\{{labor_cost}}</td></tr><tr><td style="border:1px solid #e5e7eb;padding:8px 12px;">Materials</td><td style="border:1px solid #e5e7eb;padding:8px 12px;text-align:right;font-family:monospace;">$\{{materials_cost}}</td></tr><tr style="background:#f9fafb;"><td style="border:1px solid #e5e7eb;padding:8px 12px;font-weight:700;">TOTAL</td><td style="border:1px solid #e5e7eb;padding:8px 12px;text-align:right;font-weight:700;font-family:monospace;">$\{{total_cost}}</td></tr></tbody></table>`);
  };

  const insertHourlyTable = () => {
    execCmd('insertHTML', `<table style="width:100%;border-collapse:collapse;margin:16px 0;font-size:13px;"><thead><tr style="background:#f9fafb;"><th style="border:1px solid #e5e7eb;padding:8px 12px;text-align:left;font-weight:600;">Description</th><th style="border:1px solid #e5e7eb;padding:8px 12px;text-align:center;width:70px;font-weight:600;">Hours</th><th style="border:1px solid #e5e7eb;padding:8px 12px;text-align:right;width:100px;font-weight:600;">Rate</th><th style="border:1px solid #e5e7eb;padding:8px 12px;text-align:right;width:110px;font-weight:600;">Amount</th></tr></thead><tbody><tr><td style="border:1px solid #e5e7eb;padding:8px 12px;">{{service_description}}</td><td style="border:1px solid #e5e7eb;padding:8px 12px;text-align:center;font-family:monospace;">{{duration}}</td><td style="border:1px solid #e5e7eb;padding:8px 12px;text-align:right;font-family:monospace;">$\{{hourly_rate}}/hr</td><td style="border:1px solid #e5e7eb;padding:8px 12px;text-align:right;font-family:monospace;">$\{{labor_cost}}</td></tr><tr style="background:#f9fafb;"><td colspan="3" style="border:1px solid #e5e7eb;padding:8px 12px;font-weight:700;">TOTAL</td><td style="border:1px solid #e5e7eb;padding:8px 12px;text-align:right;font-weight:700;font-family:monospace;">$\{{total_cost}}</td></tr></tbody></table>`);
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Tab') {
      e?.preventDefault();
      execCmd('insertHTML', '&nbsp;&nbsp;&nbsp;&nbsp;');
    }
  };

  const buildVariableSystemPrompt = () => {
    const allVars = VARIABLE_CATEGORIES?.flatMap(c =>
      c?.vars?.map(v => `${v?.key} — ${v?.label}: ${v?.hint}`)
    )?.join('\n');

    return `You are an expert contract editor for a moving/service company CRM system. Your job is to analyze HTML contract/document content and insert the correct template variables in the right places.

Available variables (use EXACTLY these keys including double curly braces):
${allVars}

CRITICAL RULES:
1. Return ONLY the updated HTML — no explanations, no markdown code blocks, no extra commentary
2. Replace specific hardcoded values (names, dates, amounts, addresses, phone numbers, etc.) with the appropriate {{variable}}
3. Keep ALL original HTML tags, structure, tables, formatting EXACTLY intact — only replace text content values
4. Only insert variables where they clearly belong — do not over-insert
5. DATE VARIABLES — use the correct one:
   - {{date}} = the date the document/contract is created (e.g. "Date serv.Req'd", document date, today's date)
   - {{scheduled_date}} = the date the service is scheduled/requested (e.g. "Date serv.Req'd", service date)
   - {{delivery_date}} = the requested delivery date (e.g. "Delivery Date Req'd")
   - {{due_date}} = payment due date
6. MOVING COMPANY specifics:
   - "Moving From" address → {{move_from}} or {{from_address}}
   - "Moving To" / destination address → {{move_to}} or {{to_address}}
   - "From name" / shipper name → {{client_name}}
   - Company name at top → {{company_name}}
   - "Date serv.Req'd" → {{scheduled_date}}
   - "Delivery Date Req'd" → {{delivery_date}}
7. PRICING:
   - Total amount / grand total → {{total_cost}} or {{total}}
   - Maximum amount due → {{max_amount_due}}
   - Balance due → {{balance_due}}
   - Hourly rate → {{hourly_rate}}
   - Hours → {{duration}}
8. Do NOT replace signature lines, blank underscores, or placeholder lines
9. Do NOT replace the variable keys themselves if they are already present as {{variable}}
10. Return valid HTML that preserves all original formatting`;
  };

  const buildImproveSystemPrompt = () => {
    return `You are an expert document editor and formatter. Your job is to improve, fix, and enhance HTML documents — including contracts, agreements, estimates, and invoices.

CRITICAL RULES:
1. Return ONLY the updated HTML — no explanations, no markdown code blocks, no extra commentary
2. Preserve ALL existing template variables (e.g. {{client_name}}, {{total_cost}}) — never remove or alter them
3. Keep the document's core content and legal meaning intact
4. Fix HTML structure issues — broken tags, malformed tables, missing closing tags
5. For tables: ensure proper <thead>, <tbody>, <tr>, <td>, <th> structure with consistent borders and padding
6. For text: fix grammar, spelling, punctuation, and improve professional tone where instructed
7. For formatting: fix inconsistent spacing, paragraph breaks, heading hierarchy
8. Return valid, well-structured HTML
9. Preserve all signature lines, blank underscores, and form fields
10. Do NOT add new sections or content unless explicitly asked`;
  };

  const handleAIAssist = async () => {
    const currentContent = editorRef?.current?.innerHTML || '';
    if (!currentContent?.trim() || currentContent === '<br>') {
      toast?.error('Please add some document content first');
      return;
    }
    if (!aiInstruction?.trim()) {
      toast?.error('Please describe what you want the AI to do');
      return;
    }

    setAiProcessing(true);
    try {
      const systemPrompt = aiMode === 'variables' ? buildVariableSystemPrompt() : buildImproveSystemPrompt();

      const userMessage = aiMode === 'variables'
        ? `Here is the HTML contract/document:\n\n${currentContent}\n\nUser instruction: ${aiInstruction}\n\nPlease analyze this HTML document and insert the appropriate template variables where hardcoded values appear. Return only the updated HTML with variables inserted, preserving all formatting.`
        : `Here is the HTML document:\n\n${currentContent}\n\nUser instruction: ${aiInstruction}\n\nPlease apply the requested improvements to this document. Return only the updated HTML, preserving all template variables and core content.`;

      const result = await getChatCompletion(
        'ANTHROPIC',
        'claude-sonnet-4-6',
        [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userMessage }
        ],
        { temperature: 0.2, max_tokens: 8000 }
      );

      // Check for Lambda error response
      if (result?.error) {
        throw new Error(result.error);
      }

      let updatedHtml = result?.choices?.[0]?.message?.content?.trim();
      if (!updatedHtml) throw new Error('No response from AI');

      // Strip markdown code blocks if AI wrapped in them
      updatedHtml = updatedHtml
        ?.replace(/^```html\s*/i, '')
        ?.replace(/^```\s*/i, '')
        ?.replace(/\s*```$/i, '')
        ?.trim();

      // Highlight variable chips in the returned HTML
      updatedHtml = updatedHtml?.replace(/\{\{([^}]+)\}\}/g, (match) => {
        return `<span class="var-chip" style="background:#eff6ff;color:#2563eb;border:1px solid #bfdbfe;border-radius:4px;padding:1px 6px;font-size:12px;font-family:monospace;white-space:nowrap;" contenteditable="false">${match}</span>`;
      });

      if (editorRef?.current) {
        editorRef.current.innerHTML = updatedHtml;
        isInternalChange.current = true;
        onChange(updatedHtml);
        setTimeout(() => { isInternalChange.current = false; }, 0);
      }

      const successMsg = aiMode === 'variables'
        ? 'Variables inserted successfully!' :'Document improved successfully!';
      toast?.success(successMsg);
      setShowAIPanel(false);
      setAiInstruction('');
    } catch (err) {
      toast?.error(err?.message || 'AI assistant failed. Please try again.');
    } finally {
      setAiProcessing(false);
    }
  };

  const activeCat = VARIABLE_CATEGORIES?.find(c => c?.id === activeVarCategory) || VARIABLE_CATEGORIES?.[0];
  const currentSuggestions = aiMode === 'variables' ? VARIABLE_SUGGESTIONS : IMPROVE_SUGGESTIONS;

  return (
    <div className="border border-border rounded-xl overflow-hidden bg-background">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 bg-muted/40 border-b border-border">
        <select
          onChange={e => execCmd('formatBlock', e?.target?.value)}
          className="h-7 px-2 text-xs border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary mr-1"
          defaultValue="p"
        >
          {HEADING_OPTIONS?.map(h => (
            <option key={h?.tag} value={h?.tag}>{h?.label}</option>
          ))}
        </select>

        {TOOLBAR_GROUPS?.map((group, gi) => (
          <React.Fragment key={gi}>
            {gi > 0 && <div className="w-px h-5 bg-border mx-0.5" />}
            {group?.map(btn => (
              <button
                key={btn?.cmd}
                type="button"
                title={btn?.title}
                onMouseDown={e => { e?.preventDefault(); execCmd(btn?.cmd); }}
                className={`p-1.5 rounded transition-colors ${
                  activeFormats?.[btn?.cmd]
                    ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={btn?.icon} size={14} />
              </button>
            ))}
          </React.Fragment>
        ))}

        <div className="w-px h-5 bg-border mx-0.5" />

        <button
          type="button"
          title="Insert Divider"
          onMouseDown={e => { e?.preventDefault(); insertDivider(); }}
          className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
        >
          <Icon name="Minus" size={14} />
        </button>
        <button
          type="button"
          title="Insert Generic Table"
          onMouseDown={e => { e?.preventDefault(); insertTable(); }}
          className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
        >
          <Icon name="Table" size={14} />
        </button>
        <button
          type="button"
          title="Insert Flat Rate Pricing Table (auto-fills from job)"
          onMouseDown={e => { e?.preventDefault(); insertFlatPricingTable(); }}
          className="p-1.5 text-muted-foreground hover:text-green-600 hover:bg-green-50 rounded transition-colors"
        >
          <Icon name="DollarSign" size={14} />
        </button>
        <button
          type="button"
          title="Insert Hourly Pricing Table (auto-fills from job)"
          onMouseDown={e => { e?.preventDefault(); insertHourlyTable(); }}
          className="p-1.5 text-muted-foreground hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
        >
          <Icon name="Clock" size={14} />
        </button>
        <button
          type="button"
          title="Insert Signature Line"
          onMouseDown={e => { e?.preventDefault(); insertSignatureLine(); }}
          className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
        >
          <Icon name="PenLine" size={14} />
        </button>

        <div className="w-px h-5 bg-border mx-0.5" />

        <button
          type="button"
          title="Insert Smart Variable"
          onMouseDown={e => { e?.preventDefault(); saveSelection(); setShowVarPanel(v => !v); if (showAIPanel) setShowAIPanel(false); }}
          className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-semibold transition-colors ${
            showVarPanel ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary hover:bg-primary/20'
          }`}
        >
          <Icon name="Braces" size={13} />
          Variables
        </button>

        <button
          type="button"
          title="AI Document Assistant — insert variables, fix tables, improve text"
          onMouseDown={e => { e?.preventDefault(); setShowAIPanel(v => !v); if (showVarPanel) setShowVarPanel(false); }}
          className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-semibold transition-colors ${
            showAIPanel ? 'bg-violet-600 text-white' : 'bg-violet-50 text-violet-700 hover:bg-violet-100 border border-violet-200'
          }`}
        >
          <Icon name="Sparkles" size={13} />
          AI Assistant
        </button>
      </div>
      {/* AI Assistant Panel */}
      {showAIPanel && (
        <div className="border-b border-border bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/30">
          <div className="px-4 pt-3 pb-1">
            {/* Header */}
            <div className="flex items-center gap-2 mb-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-violet-600 flex items-center justify-center">
                <Icon name="Sparkles" size={13} className="text-white" />
              </div>
              <div>
                <span className="text-sm font-semibold text-violet-800 dark:text-violet-300">AI Document Assistant</span>
              </div>
            </div>

            {/* Mode Tabs */}
            <div className="flex gap-1 mb-3 bg-white dark:bg-slate-800 rounded-lg p-1 border border-violet-100 dark:border-violet-800 w-fit">
              {AI_MODES?.map(mode => (
                <button
                  key={mode?.id}
                  type="button"
                  onClick={() => { setAiMode(mode?.id); setAiInstruction(''); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                    aiMode === mode?.id
                      ? 'bg-violet-600 text-white shadow-sm'
                      : 'text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/30'
                  }`}
                >
                  <Icon name={mode?.icon} size={12} />
                  {mode?.label}
                </button>
              ))}
            </div>

            {/* Mode description */}
            <p className="text-xs text-violet-700 dark:text-violet-400 mb-3 leading-relaxed">
              {aiMode === 'variables'
                ? 'AI will scan your document and automatically insert the correct template variables (e.g. {{client_name}}, {{total_cost}}) wherever hardcoded values appear.' :'AI will fix and improve your document — repair broken tables, fix formatting, improve text quality, clean up structure, and make it look professional.'}
            </p>

            {/* Input + Button */}
            <div className="flex gap-2">
              <input
                type="text"
                value={aiInstruction}
                onChange={e => setAiInstruction(e?.target?.value)}
                onKeyDown={e => { if (e?.key === 'Enter' && !aiProcessing) handleAIAssist(); }}
                placeholder={
                  aiMode === 'variables'
                    ? 'e.g. "Insert all variables" or "Add pricing variables to payment section"'
                    : 'e.g. "Fix all tables and improve text quality" or "Make it more professional"'
                }
                className="flex-1 text-sm px-3 py-2 rounded-lg border border-violet-200 bg-white dark:bg-slate-800 dark:border-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-400 placeholder:text-slate-400"
                disabled={aiProcessing}
              />
              <button
                type="button"
                onClick={handleAIAssist}
                disabled={aiProcessing || !aiInstruction?.trim()}
                className="flex items-center gap-1.5 px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors whitespace-nowrap"
              >
                {aiProcessing ? (
                  <>
                    <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Processing…
                  </>
                ) : (
                  <>
                    <Icon name="Wand2" size={13} />
                    Apply
                  </>
                )}
              </button>
            </div>

            {/* Quick suggestions */}
            <div className="flex flex-wrap gap-1.5 mt-2 pb-3">
              {aiMode === 'variables'
                ? VARIABLE_SUGGESTIONS?.map(suggestion => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => setAiInstruction(suggestion)}
                      className="text-xs px-2 py-1 rounded-md bg-white dark:bg-slate-800 border border-violet-200 text-violet-600 hover:bg-violet-50 transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))
                : IMPROVE_SUGGESTIONS?.map(s => (
                    <button
                      key={s?.label}
                      type="button"
                      onClick={() => setAiInstruction(s?.prompt)}
                      className="text-xs px-2 py-1 rounded-md bg-white dark:bg-slate-800 border border-violet-200 text-violet-600 hover:bg-violet-50 transition-colors"
                    >
                      {s?.label}
                    </button>
                  ))
              }
            </div>
          </div>
        </div>
      )}
      {/* Variable Panel */}
      {showVarPanel && (
        <div className="border-b border-border bg-slate-50 dark:bg-slate-900/40">
          {/* Category tabs */}
          <div className="flex border-b border-border/60 px-3 pt-2 gap-0.5 overflow-x-auto">
            {VARIABLE_CATEGORIES?.map(cat => (
              <button
                key={cat?.id}
                type="button"
                onMouseDown={e => { e?.preventDefault(); setActiveVarCategory(cat?.id); }}
                className={`px-3 py-1.5 text-xs font-medium rounded-t-lg border-b-2 transition-colors whitespace-nowrap -mb-px ${
                  activeVarCategory === cat?.id
                    ? 'border-primary text-primary bg-white dark:bg-slate-800 border-l border-r border-t border-border' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {cat?.label}
              </button>
            ))}
          </div>

          {activeCat?.description && (
            <div className="px-3 py-2 flex items-center gap-1.5 bg-blue-50 border-b border-blue-100">
              <Icon name="Zap" size={11} className="text-blue-500 shrink-0" />
              <span className="text-xs text-blue-700 font-medium">{activeCat?.description}</span>
            </div>
          )}

          <div className="p-3 grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-48 overflow-y-auto">
            {activeCat?.vars?.map(v => (
              <button
                key={v?.key}
                type="button"
                onMouseDown={e => { e?.preventDefault(); insertVariable(v?.key); }}
                className="flex flex-col items-start px-2.5 py-2 rounded-lg border border-border bg-white dark:bg-slate-800 hover:border-primary/60 hover:bg-primary/5 transition-all text-left group"
              >
                <span className="text-xs font-mono text-blue-600 font-semibold group-hover:text-primary leading-tight truncate w-full">{v?.key}</span>
                <span className="text-xs text-muted-foreground mt-0.5 leading-tight">{v?.hint}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Editable Area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onKeyUp={updateActiveFormats}
        onMouseUp={updateActiveFormats}
        onFocus={updateActiveFormats}
        onBlur={saveSelection}
        data-placeholder={placeholder}
        className="outline-none px-5 py-4 text-sm text-foreground leading-relaxed overflow-y-auto"
        style={{ minHeight: `${minHeight}px`, maxHeight: '600px' }}
      />
      <style>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
          white-space: pre-wrap;
        }
        [contenteditable] h1 { font-size: 1.5rem; font-weight: 700; margin: 10px 0 6px; }
        [contenteditable] h2 { font-size: 1.15rem; font-weight: 600; margin: 8px 0 4px; }
        [contenteditable] h3 { font-size: 1rem; font-weight: 600; margin: 6px 0 3px; }
        [contenteditable] p { margin: 3px 0; }
        [contenteditable] ul { list-style: disc; padding-left: 20px; margin: 4px 0; }
        [contenteditable] ol { list-style: decimal; padding-left: 20px; margin: 4px 0; }
        [contenteditable] li { margin: 2px 0; }
        [contenteditable] table { width: 100%; border-collapse: collapse; margin: 8px 0; }
        [contenteditable] td, [contenteditable] th { border: 1px solid #e5e7eb; padding: 5px 8px; }
        [contenteditable] hr { border: none; border-top: 2px solid #e5e7eb; margin: 10px 0; }
        [contenteditable] strong, [contenteditable] b { font-weight: 700; }
        [contenteditable] em, [contenteditable] i { font-style: italic; }
        [contenteditable] u { text-decoration: underline; }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
